import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { useGetAllStaffQuery } from "../../../store/appApi";
import {
  selectUser,
  selectUserModules,
  selectUserPagePermissions,
} from "../../../store/authSlice";
import { getMasterCustomersApi } from "../../../services/staffReporting";
import {
  formatDateLabel,
  formatRoleLabel,
  getFormTypeForRole,
} from "./reportingConfig";
import { exportTeamEffortsToExcel } from "./exportStaffReportExcel";

const ROSTER_ROLES = ["Tele Calling", "Real Estate Sales"];
const PAGE_KEY = "workforce.staffReporting.efforts";
const LEVELS = { none: 0, read: 1, "read-write": 2 };

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";

const TeamEfforts = () => {
  const me = useSelector(selectUser);
  const modules = useSelector(selectUserModules);
  const pagePermissions = useSelector(selectUserPagePermissions);
  const isSuperAdmin = me?.role === "super-admin";
  // A page permission of "inherit" (the default) falls back to the Workforce
  // module's own level — same resolution the sidebar uses to decide whether
  // to show the link at all.
  const explicitPageLevel = pagePermissions?.[PAGE_KEY];
  const effectiveLevel =
    explicitPageLevel && explicitPageLevel !== "inherit"
      ? explicitPageLevel
      : (modules?.workforce ?? "none");
  const canView = isSuperAdmin || (LEVELS[effectiveLevel] ?? 0) >= LEVELS.read;

  const { data: staffData, isFetching: staffLoading } = useGetAllStaffQuery(
    { page: 1, limit: 500 },
    { skip: !canView },
  );
  const rosterStaff = useMemo(
    () =>
      (Array.isArray(staffData?.staff) ? staffData.staff : [])
        .filter((s) => s?.status !== false && ROSTER_ROLES.includes(s?.role))
        .sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [staffData],
  );

  const [rosterByEmployeeId, setRosterByEmployeeId] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Employee ids whose "customers with call history" list is expanded.
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  // Customer whose full call-log history modal is open (+ owning employee).
  const [historyView, setHistoryView] = useState(null);

  const toggleExpanded = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Sorted (newest first) dated remark / call history for one customer.
  const customerHistory = (customer) =>
    (Array.isArray(customer?.dailyRemarks) ? [...customer.dailyRemarks] : []).sort(
      (a, b) => String(b.date).localeCompare(String(a.date)),
    );

  useEffect(() => {
    if (!canView || !rosterStaff.length) return;
    let cancelled = false;
    setLoading(true);
    Promise.all(
      rosterStaff.map((s) =>
        getMasterCustomersApi(getFormTypeForRole(s.role), s._id)
          .then((res) => [String(s._id), res?.data?.customers || []])
          .catch(() => [String(s._id), []]),
      ),
    )
      .then((pairs) => {
        if (cancelled) return;
        setRosterByEmployeeId(new Map(pairs));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [canView, rosterStaff]);

  const rows = rosterStaff.map((s) => {
    const customers = rosterByEmployeeId.get(String(s._id)) || [];
    const active = customers.filter((c) => !c.archived).length;
    const archived = customers.length - active;
    const remarkDates = customers.flatMap((c) =>
      (Array.isArray(c.dailyRemarks) ? c.dailyRemarks : []).map((r) => r.date),
    );
    const lastActivity = [...remarkDates].sort().slice(-1)[0];
    // Customers this employee has actually logged calls / remarks against,
    // most-recently-worked first — the ones a super-admin wants to inspect.
    const worked = customers
      .filter(
        (c) => Array.isArray(c.dailyRemarks) && c.dailyRemarks.length > 0,
      )
      .map((c) => {
        const history = customerHistory(c);
        return { customer: c, history, last: history[0] };
      })
      .sort((a, b) =>
        String(b.last?.date || "").localeCompare(String(a.last?.date || "")),
      );
    return {
      employee: s,
      active,
      archived,
      totalRemarks: remarkDates.length,
      lastActivity,
      worked,
    };
  });

  const handleExport = async () => {
    try {
      setExporting(true);
      await exportTeamEffortsToExcel({
        employees: rosterStaff,
        rosterByEmployeeId,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Export failed",
        text: error?.message || "The report could not be generated.",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Team Efforts" motherMenu="Workforce" />
      <div className="space-y-6">
        <section className={`${panelClass} p-6`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                Team Efforts Report
              </div>
              <h1 className="mt-3 text-2xl font-semibold text-gray-900">
                Work Done by Employee
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                A roll-up of every Tele Calling / Real Estate Sales employee's
                Customer Database — active vs. archived customers, remarks
                logged, and when they last worked a customer. Download the
                full call log for every employee as one Excel workbook.
              </p>
            </div>
            <button
              type="button"
              className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleExport}
              disabled={exporting || loading || !rosterStaff.length}
            >
              {exporting ? "Preparing…" : "Download Excel (All Employees)"}
            </button>
          </div>
        </section>

        {!canView ? (
          <section className={`${panelClass} p-8 text-center text-gray-500`}>
            You don't have access to the Team Efforts Report.
          </section>
        ) : (
          <section className={`${panelClass} p-5 md:p-6`}>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="whitespace-nowrap px-3 py-2 text-left font-medium">
                      Employee
                    </th>
                    <th className="whitespace-nowrap px-3 py-2 text-left font-medium">
                      Role
                    </th>
                    <th className="whitespace-nowrap px-3 py-2 text-left font-medium">
                      Active Customers
                    </th>
                    <th className="whitespace-nowrap px-3 py-2 text-left font-medium">
                      Archived
                    </th>
                    <th className="whitespace-nowrap px-3 py-2 text-left font-medium">
                      Total Remarks
                    </th>
                    <th className="whitespace-nowrap px-3 py-2 text-left font-medium">
                      Last Activity
                    </th>
                    <th className="whitespace-nowrap px-3 py-2 text-left font-medium">
                      Customers Worked
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {!rows.length && !staffLoading && (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-3 py-6 text-center text-xs text-gray-400"
                      >
                        No Tele Calling / Real Estate Sales staff found.
                      </td>
                    </tr>
                  )}
                  {rows.map(
                    ({
                      employee,
                      active,
                      archived,
                      totalRemarks,
                      lastActivity,
                      worked,
                    }) => {
                      const isOpen = expandedIds.has(String(employee._id));
                      return (
                        <React.Fragment key={employee._id}>
                          <tr
                            className={
                              worked.length
                                ? "cursor-pointer transition hover:bg-gray-50"
                                : undefined
                            }
                            onClick={() =>
                              worked.length &&
                              toggleExpanded(String(employee._id))
                            }
                          >
                            <td className="whitespace-nowrap px-3 py-2 font-medium text-gray-900">
                              {employee.name}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                              {formatRoleLabel(employee.role)}
                            </td>
                            <td className="px-3 py-2 text-gray-800">
                              {loading ? "…" : active}
                            </td>
                            <td className="px-3 py-2 text-gray-500">
                              {loading ? "…" : archived}
                            </td>
                            <td className="px-3 py-2 text-gray-800">
                              {loading ? "…" : totalRemarks}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                              {loading
                                ? "…"
                                : lastActivity
                                  ? formatDateLabel(lastActivity)
                                  : "—"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2">
                              {loading ? (
                                "…"
                              ) : worked.length ? (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                                  {worked.length} with call log
                                  <i
                                    className={`fa-solid fa-chevron-${
                                      isOpen ? "up" : "down"
                                    } text-[10px]`}
                                  />
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">
                                  — none —
                                </span>
                              )}
                            </td>
                          </tr>
                          {isOpen && worked.length > 0 && (
                            <tr>
                              <td colSpan={7} className="bg-gray-50 px-3 py-3">
                                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-50 text-gray-500">
                                      <tr>
                                        <th className="px-3 py-1.5 text-left font-medium">
                                          Customer
                                        </th>
                                        <th className="px-3 py-1.5 text-left font-medium">
                                          Phone
                                        </th>
                                        <th className="px-3 py-1.5 text-left font-medium">
                                          Calls
                                        </th>
                                        <th className="px-3 py-1.5 text-left font-medium">
                                          Latest remark
                                        </th>
                                        <th className="px-3 py-1.5 text-right font-medium">
                                          History
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                      {worked.map(({ customer, history, last }) => (
                                        <tr key={customer.id}>
                                          <td className="px-3 py-1.5 font-medium text-gray-800">
                                            {customer.name ||
                                              customer.phone ||
                                              "—"}
                                            {customer.archived && (
                                              <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
                                                archived
                                              </span>
                                            )}
                                          </td>
                                          <td className="px-3 py-1.5 text-gray-600">
                                            {customer.phone || "—"}
                                          </td>
                                          <td className="px-3 py-1.5 text-gray-800">
                                            {history.length}
                                          </td>
                                          <td className="px-3 py-1.5 text-gray-600">
                                            {last ? (
                                              <>
                                                <span className="font-medium text-gray-700">
                                                  {formatDateLabel(last.date)}
                                                </span>
                                                {last.by && (
                                                  <span className="mx-1 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-700">
                                                    {last.by}
                                                  </span>
                                                )}{" "}
                                                — {last.remarks || "(no remark)"}
                                              </>
                                            ) : (
                                              "—"
                                            )}
                                          </td>
                                          <td className="px-3 py-1.5 text-right">
                                            <button
                                              type="button"
                                              className="rounded-full border-2 border-amber-500 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700 transition hover:bg-amber-50"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setHistoryView({
                                                  customer,
                                                  employeeName: employee.name,
                                                });
                                              }}
                                            >
                                              View ({history.length})
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    },
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>

      {historyView && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setHistoryView(null)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {historyView.customer.name ||
                    historyView.customer.phone ||
                    "Customer"}
                </h3>
                <p className="mt-0.5 text-sm text-gray-500">
                  {historyView.customer.phone
                    ? `${historyView.customer.phone} · `
                    : ""}
                  {historyView.employeeName} · full call-log history
                </p>
              </div>
              <button
                type="button"
                className="rounded-full border-2 border-gray-300 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-gray-600"
                onClick={() => setHistoryView(null)}
              >
                Close
              </button>
            </div>
            <div className="mt-4 max-h-96 overflow-y-auto rounded-lg border border-gray-200">
              {customerHistory(historyView.customer).map((h, i) => (
                <div
                  key={i}
                  className={`px-4 py-2 text-sm ${
                    i % 2 === 1 ? "bg-gray-50" : "bg-white"
                  }`}
                >
                  <span className="font-medium text-gray-700">
                    {formatDateLabel(h.date)}
                  </span>{" "}
                  {h.by && (
                    <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-700">
                      {h.by}
                    </span>
                  )}{" "}
                  <span className="text-gray-600">
                    — {h.remarks || "(no remark)"}
                  </span>
                </div>
              ))}
              {customerHistory(historyView.customer).length === 0 && (
                <div className="px-4 py-3 text-sm text-gray-400">
                  No history.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TeamEfforts;
